import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { COMPANY } from "@/lib/site";

const Shell = ({ title, children, testid }) => {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <main data-testid={testid} className="w-full bg-[#0a0a0a] pt-32 pb-24 px-5 sm:px-8 lg:px-12">
      <Link
        to="/"
        data-testid="legal-back-link"
        className="inline-flex items-center gap-2 font-mono-t text-[11px] uppercase tracking-widest text-neutral-400 hover:text-[#f95700] transition-colors"
      >
        <ArrowLeft size={14} /> Zurück zur Startseite
      </Link>
      <h1 className="mt-8 font-display uppercase font-black leading-[0.9] tracking-tight text-white text-[13vw] sm:text-[8vw] lg:text-[5vw]">
        {title}
      </h1>
      <div className="hairline w-40 my-10" />
      <div className="max-w-3xl space-y-8 text-[15px] text-neutral-300 leading-relaxed">{children}</div>
      <p className="mt-14 font-mono-t text-[11px] text-neutral-600 max-w-3xl leading-relaxed">
        Hinweis: Dieser Text ist eine allgemeine Vorlage und stellt keine Rechtsberatung dar. Bitte
        lassen Sie die Angaben vor Veröffentlichung juristisch prüfen und ergänzen.
      </p>
    </main>
  );
};

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl font-bold uppercase text-white tracking-tight">{children}</h2>
);

export const Impressum = () => (
  <Shell title="Impressum" testid="impressum-page">
    <section className="space-y-3">
      <H2>Angaben gemäß § 5 DDG</H2>
      <p>
        {COMPANY.name}
        <br />
        {COMPANY.street}
        <br />
        {COMPANY.zip} {COMPANY.city}
        <br />
        {COMPANY.country}
      </p>
    </section>
    <section className="space-y-3">
      <H2>Kontakt</H2>
      <p>
        Telefon: {COMPANY.phoneDisplay}
        <br />
        E-Mail: {COMPANY.email}
      </p>
    </section>
    <section className="space-y-3">
      <H2>Öffnungszeiten</H2>
      <p>
        {COMPANY.hours.map((h) => (
          <span key={h.d} className="block">
            {h.d}: {h.t}
          </span>
        ))}
      </p>
    </section>
    <section className="space-y-3">
      <H2>Verantwortlich für den Inhalt</H2>
      <p>
        {COMPANY.name}, {COMPANY.street}, {COMPANY.zip} {COMPANY.city}
      </p>
    </section>
    <section className="space-y-3">
      <H2>Umsatzsteuer-Identifikationsnummer</H2>
      <p>Wird nach Vorliegen ergänzt.</p>
    </section>
    <section className="space-y-3">
      <H2>Haftung für Inhalte</H2>
      <p>
        Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen
        Gesetzen verantwortlich. Wir sind jedoch nicht verpflichtet, übermittelte oder gespeicherte
        fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine
        rechtswidrige Tätigkeit hinweisen.
      </p>
    </section>
    <section className="space-y-3">
      <H2>Haftung für Links</H2>
      <p>
        Unser Angebot enthält gegebenenfalls Links zu externen Webseiten Dritter, auf deren Inhalte
        wir keinen Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige
        Anbieter oder Betreiber der Seiten verantwortlich.
      </p>
    </section>
    <section className="space-y-3">
      <H2>Urheberrecht</H2>
      <p>
        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem
        deutschen Urheberrecht. Verwendete Bildmaterialien stammen teilweise von
        Stockfoto-Anbietern.
      </p>
    </section>
  </Shell>
);

export const Datenschutz = () => (
  <Shell title="Datenschutz" testid="datenschutz-page">
    <section className="space-y-3">
      <H2>1. Verantwortlicher</H2>
      <p>
        {COMPANY.name}
        <br />
        {COMPANY.street}, {COMPANY.zip} {COMPANY.city}
        <br />
        Telefon: {COMPANY.phoneDisplay}
        <br />
        E-Mail: {COMPANY.email}
      </p>
    </section>
    <section className="space-y-3">
      <H2>2. Allgemeines</H2>
      <p>
        Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung einer
        funktionsfähigen Website sowie zur Bearbeitung Ihrer Anfragen erforderlich ist. Rechtsgrundlage
        ist in der Regel Art. 6 Abs. 1 lit. b und lit. f DSGVO, bei ausdrücklicher Zustimmung Art. 6
        Abs. 1 lit. a DSGVO.
      </p>
    </section>
    <section className="space-y-3">
      <H2>3. Anfragen über Konfigurator und Anfrageformular</H2>
      <p>
        Wenn Sie uns über den Transport-Konfigurator oder das Anfrageformular kontaktieren, verarbeiten
        wir die von Ihnen angegebenen Daten (z. B. Name, Telefonnummer, E-Mail-Adresse, Abhol- und
        Lieferadresse, Art und Umfang der Ladung, Wunschtermin, freiwillige Angaben und optional
        hochgeladene Fotos), um Ihre Anfrage zu bearbeiten und Ihnen ein Angebot zu erstellen. Die Daten
        werden auf unserem Server gespeichert und nach Abschluss des Vorgangs gelöscht, sofern keine
        gesetzlichen Aufbewahrungspflichten bestehen.
      </p>
    </section>
    <section className="space-y-3">
      <H2>4. Foto-Upload</H2>
      <p>
        Optional hochgeladene Fotos werden verschlüsselt übertragen und ausschließlich zur
        Einschätzung des Transportumfangs verwendet. Bitte laden Sie keine Bilder hoch, die
        personenbezogene Daten Dritter erkennbar enthalten.
      </p>
    </section>
    <section className="space-y-3">
      <H2>5. Kontaktaufnahme über WhatsApp</H2>
      <p>
        Wenn Sie einen WhatsApp-Button nutzen, werden Sie zu WhatsApp weitergeleitet. Dabei werden
        Daten (u. a. Ihre Telefonnummer und der Nachrichteninhalt) an WhatsApp Ireland Ltd.
        übermittelt und dort nach den Datenschutzbestimmungen von WhatsApp verarbeitet. Eine
        Übermittlung in Drittländer kann nicht ausgeschlossen werden. Die Nutzung ist freiwillig;
        alternativ erreichen Sie uns per Telefon, E-Mail oder über das Kontaktformular.
      </p>
    </section>
    <section className="space-y-3">
      <H2>6. Google Maps</H2>
      <p>
        Die Kartendarstellung wird erst nach Ihrem ausdrücklichen Klick auf „Karte laden" geladen.
        Erst dann werden Daten (u. a. Ihre IP-Adresse) an Google Ireland Ltd. übermittelt. Ohne Ihre
        Zustimmung findet keine Verbindung zu Google statt.
      </p>
    </section>
    <section className="space-y-3">
      <H2>7. Server-Logfiles</H2>
      <p>
        Beim Aufruf unserer Website werden technisch notwendige Zugriffsdaten verarbeitet
        (IP-Adresse, Datum und Uhrzeit, aufgerufene Seite, Browsertyp). Dies dient dem sicheren und
        stabilen Betrieb der Website.
      </p>
    </section>
    <section className="space-y-3">
      <H2>8. Ihre Rechte</H2>
      <p>
        Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung,
        Datenübertragbarkeit sowie Widerspruch gegen die Verarbeitung. Eine erteilte Einwilligung
        können Sie jederzeit widerrufen. Zudem steht Ihnen ein Beschwerderecht bei der zuständigen
        Datenschutzaufsichtsbehörde zu.
      </p>
    </section>
    <section className="space-y-3">
      <H2>9. Externe Bildmaterialien</H2>
      <p>
        Zur Illustration werden Bilder von Stockfoto-Diensten eingebunden. Dabei kann Ihre IP-Adresse
        an den jeweiligen Anbieter übermittelt werden.
      </p>
    </section>
  </Shell>
);
